import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { colors, LoadingSpinner } from '../shared';
import { ShieldX } from 'lucide-react';

// GOD MODE: These emails ALWAYS have full admin access, no questions asked
const GOD_MODE_EMAILS = [
  'aj@snshn.co',
];

// Full permissions for god mode and admin users
const FULL_PERMISSIONS = {
  managePrompts: true,
  viewPrompts: true,
  viewAnalytics: true,
  viewFeedback: true,
  manageAdmins: true,
  manageUsers: true,
  manageCards: true,
};

export default function AdminGuard({ children }) {
  const { user, userProfile, loading: authLoading, profileLoading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (authLoading || profileLoading) return;

      if (!user) {
        setChecking(false);
        setIsAdmin(false);
        return;
      }

      // GOD MODE CHECK - bypass everything for owner
      if (GOD_MODE_EMAILS.includes(user.email?.toLowerCase())) {
        setIsAdmin(true);
        setPermissions(FULL_PERMISSIONS);
        setChecking(false);
        return;
      }

      // Check user profile role first (new system)
      if (userProfile?.role === 'admin') {
        setIsAdmin(true);
        setPermissions(FULL_PERMISSIONS);
        setChecking(false);
        return;
      }

      // Fallback: Check legacy admin_whitelist collection
      try {
        const adminDoc = await getDoc(doc(db, 'admin_whitelist', user.email));

        if (adminDoc.exists()) {
          setIsAdmin(true);
          setPermissions(adminDoc.data().permissions || FULL_PERMISSIONS);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin access:', error);
        setIsAdmin(false);
      } finally {
        setChecking(false);
      }
    };

    checkAdminAccess();
  }, [user, userProfile, authLoading, profileLoading]);

  if (authLoading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.black }}>
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4" style={{ color: colors.textMuted }}>Checking access...</p>
        </div>
      </div>
    );
  }

  // Note: !user case is handled by App.jsx auth gate — user is always authenticated here

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.black }}>
        <div className="text-center max-w-md px-6">
          <div className="p-4 rounded-full inline-block mb-4" style={{ background: colors.redDim }}>
            <ShieldX size={48} style={{ color: colors.red }} />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: colors.textPrimary }}>
            Access Denied
          </h1>
          <p className="mb-2" style={{ color: colors.textSecondary }}>
            You don't have permission to access the admin panel.
          </p>
          <p className="text-sm mb-6" style={{ color: colors.textMuted }}>
            Logged in as: {user.email}
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 rounded-xl font-semibold"
            style={{ background: colors.yellow, color: colors.black }}
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  // Pass permissions to children
  return typeof children === 'function'
    ? children({ permissions, user })
    : children;
}
